import React from 'react';
import { NavLink } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { lineHeight, theme, toRem } from 'design';
import { Icon } from '../Icon';
import { NavItemProps } from './NavItemProps';

const StyledNavLink = styled(NavLink)`
  display: flex;
  flex-shrink: 0;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  height: auto;
  margin-bottom: ${toRem(theme.space.md)};
  text-decoration: none;
  cursor: pointer;
  overflow: hidden;

  &:active,
  &:visited {
    text-decoration: none;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const Layout = styled.div<Partial<NavItemProps>>`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  height: ${toRem(44)};
  padding: 0 ${toRem(theme.space.md)};
  border-radius: ${toRem(11)};
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.medium};
  font-size: ${toRem(theme.font.size.sm)};
  line-height: ${lineHeight(theme.font.size.sm)};
  text-decoration: none;
  color: ${theme.color.white};
  transition: all ${theme.transition.button.default};
  overflow: hidden;
  padding: 0 ${toRem(theme.space.md)};
  gap: ${toRem(theme.space.md)};

  svg {
    fill: ${theme.color.white};
  }

  &:hover {
    background-color: rgba(255, 255, 255, 0.11);
  }

  @media (min-width: ${theme.breakpoint.small}px) {
    width: ${toRem(44)};
    color: ${theme.color.neutral[700]};

    &:hover {
      background-color: ${theme.color.neutral[100]};
    }

    svg {
      fill: ${theme.color.neutral[700]};
    }
  }

  ${({ navOpen }) =>
    navOpen &&
    css`
      @media (min-width: ${theme.breakpoint.small}px) {
        width: ${toRem(theme.nav.width.open - 2 * theme.space.md)};
      }
    `}

  ${({ itemActive }) =>
    itemActive &&
    css`
      background-color: rgba(255, 255, 255, 0.11);
      font-weight: ${theme.font.weight.semiBold};
      color: ${theme.color.white};

      @media (min-width: ${theme.breakpoint.small}px) {
        background-color: ${theme.color.neutral[100]};
        color: ${theme.color.neutral[700]};

        svg {
          fill: ${theme.color.neutral[700]};
        }
      }
    `}
`;

const StyledIcon = styled(Icon)<Partial<NavItemProps>>`
  display: flex;
  flex-shrink: 0;
`;

const Label = styled.span<Partial<NavItemProps>>`
  display: flex;
  opacity: 0;
  transition: opacity ${theme.transition.nav};

  ${({ navOpen }) =>
    navOpen &&
    css`
      opacity: 1;
    `}
`;

const NavItem = ({ className, exact = false, icon, iconActive, itemActive, label, navOpen, onClick, to }: NavItemProps) => {
  const getIcon = () => {
    if (icon && iconActive) {
      return <StyledIcon navOpen={navOpen} icon={itemActive ? iconActive : icon} width={18} height={18} />;
    }
    if (icon && !iconActive) {
      return <StyledIcon navOpen={navOpen} icon={icon} width={18} height={18} />;
    }
    return null;
  };

  return (
    <StyledNavLink aria-label={`Navigate to ${label}`} className={className} exact={exact} onClick={onClick} to={to}>
      <Layout navOpen={navOpen} itemActive={itemActive}>
        {getIcon()}
        <Label navOpen={navOpen}>{label}</Label>
      </Layout>
    </StyledNavLink>
  );
};

export default NavItem;
